import RefreshIcon from 'common/assets/refresh.svg';
import { seconds2timestring } from 'common/utils/seconds2timestring';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import WaveSurfer from 'wavesurfer.js';
import { Translate } from 'i18n';
import { ActionButton } from './ActionButton';
import { AudioState } from './types';
import { recorderErrorMessage } from './recorderErrorMessage';
import { recordingStatusEnum, useAudioRecorder } from './useAudioRecorder';
import { useSpeechAudioRecorder } from './useSpeechAudioRecorder';

interface IAudioRecorderProps {
  onRecordChange: (record: Blob | null) => void;
  customOptions?: any;
  submitButton?: React.ReactNode;
  mode?: 'default' | 'speech';
}

export const AudioRecorder: React.FC<IAudioRecorderProps> = ({
  onRecordChange,
  customOptions,
  submitButton,
  mode = 'default'
}) => {
  const [audioSrc, setAudioSrc] = useState<string | null>(null);
  const [state, setState] = useState<AudioState>(AudioState.Initial);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const waveform = useRef<WaveSurfer | null>(null);
  const intervalRef = useRef<number | null>(null);

  const handleRecordChange = useCallback(
    (src: string | any, audioBlob: Blob | any) => {
      setAudioSrc(src);
      onRecordChange(audioBlob);
    },
    [onRecordChange]
  );

  const handleRecordError = useCallback((errorMessage: string) => {
    setErrorMessage(recorderErrorMessage(errorMessage));
  }, []);

  // Keep the latest customOptions available to the waveform effect without making
  // it a dependency — options are static, and a fresh object literal from a parent
  // must not force WaveSurfer to be destroyed/recreated on every render.
  const customOptionsRef = useRef(customOptions);
  customOptionsRef.current = customOptions;

  const hookArgs = {
    setDuration,
    onRecordChange: handleRecordChange,
    onError: handleRecordError
  };

  // Both hooks are always called (React rules of hooks require stable call order).
  // Only the active hook's controls are wired to the UI.
  // IMPORTANT: this is safe because neither hook acquires resources (mic, AudioContext)
  // on mount — only when startRecording() is called. Adding mount-time side effects
  // to either hook would break this assumption.
  const defaultRecorder = useAudioRecorder(hookArgs);
  const speechRecorder = useSpeechAudioRecorder(hookArgs);

  const [status, seconds, , { startRecording, finishRecording, resetRecording }] =
    mode === 'speech' ? speechRecorder : defaultRecorder;

  const waveformId = mode === 'speech' ? 'speechrecordwaveform' : 'recordwaveform';

  // Create/load WaveSurfer once per recording (audioSrc). Depending on
  // customOptions here caused the instance to be destroyed + recreated on every
  // render (a fresh object literal from the parent), which re-measured the
  // container mid-layout and made the waveform render broken and jump.
  useEffect(() => {
    if (!audioSrc) {
      waveform.current && waveform.current.destroy();
      waveform.current = null;
      setState(AudioState.Initial);
      setCurrentTime(0);
      return undefined;
    }

    const ws = WaveSurfer.create({
      barWidth: 1,
      barGap: 2,
      cursorWidth: 1,
      container: `#${waveformId}`,
      height: 60,
      progressColor: '#00A5D7',
      waveColor: '#807F91',
      cursorColor: 'transparent',
      // WaveSurfer v7: fit the clip to the container instead of a fixed
      // px-per-second zoom that overflows and auto-scrolls (the jerk/blink).
      fillParent: true,
      autoScroll: false,
      hideScrollbar: true,
      ...customOptionsRef.current
    });
    waveform.current = ws;
    ws.load(audioSrc);

    ws.on('seeking', function () {
      setState(AudioState.Pausing);
      setCurrentTime(ws.getCurrentTime());
    });
    ws.on('finish', function () {
      setState(AudioState.Pausing);
    });

    return () => {
      ws.destroy();
      if (waveform.current === ws) waveform.current = null;
    };
    // customOptionsRef is intentionally read via ref, not listed as a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioSrc, waveformId]);

  useEffect(() => {
    if (state === AudioState.Playing) {
      intervalRef.current = window.setInterval(() => {
        if (waveform.current) setCurrentTime(waveform.current.getCurrentTime());
      }, 1000);
    } else {
      intervalRef?.current && clearInterval(intervalRef.current);
    }

    return () => {
      intervalRef.current && clearInterval(intervalRef.current);
    };
  }, [state]);

  const handleActionButtonClick = useCallback(() => {
    const handlePlay = () => {
      if (waveform.current) {
        waveform.current.playPause();
        switch (state) {
          case AudioState.Initial: {
            setState(AudioState.Playing);
            break;
          }
          case AudioState.Playing: {
            setState(AudioState.Pausing);
            break;
          }
          case AudioState.Pausing: {
            setState(AudioState.Playing);
            break;
          }
          default:
            break;
        }
      }
    };

    type tActionMapType = {
      [index in recordingStatusEnum]: () => void;
    };

    function _startRecording() {
      setErrorMessage('');
      startRecording();
    }

    const actionMap: tActionMapType = {
      [recordingStatusEnum.EMPTY]: _startRecording,
      [recordingStatusEnum.RECORDING]: finishRecording,
      [recordingStatusEnum.PAUSED]: finishRecording,
      [recordingStatusEnum.FINISHED]: handlePlay
    };
    actionMap[status]();
  }, [status, startRecording, finishRecording, state]);

  const handleDeleteClick = useCallback(() => {
    setAudioSrc(null);
    onRecordChange(null);
    resetRecording();
  }, [resetRecording, setAudioSrc, onRecordChange]);

  const resetBtn = (
    <button
      type="button"
      className="ln-flex ln-items-center ln-bg-[#0B0D0F1A] ln-px-8 ln-py-3 ln-gap-2 ln-p-1.5 ln-rounded-[16px] ln-h-12"
      onClick={handleDeleteClick}
    >
      <img src={RefreshIcon} alt="new recording" />
      <p className="ln-font-bold ln-text-black">
        <Translate str="frontend.lesson_task.check.assignment.re_record" />
      </p>
    </button>
  );

  return (
    <SFlexColumnDiv status={status}>
      {audioSrc && (
        <SWaveformContainer>
          <SWave id={waveformId} />
        </SWaveformContainer>
      )}
      {!!errorMessage && <SRecordMessage>{errorMessage}</SRecordMessage>}
      <SRecordBtnGroup status={status}>
        <ActionButton
          status={status}
          audioPlayStatus={state}
          onClick={handleActionButtonClick}
          node={
            <SRecordingStatus>
              {status !== recordingStatusEnum.EMPTY && state === AudioState.Initial && (
                <STime status={status}>{seconds2timestring(seconds || duration)}</STime>
              )}
              {status === recordingStatusEnum.FINISHED &&
                state !== AudioState.Initial && (
                  <STime status={status}>{seconds2timestring(currentTime)}</STime>
                )}
            </SRecordingStatus>
          }
        />
      </SRecordBtnGroup>
      {status === recordingStatusEnum.FINISHED && (
        <SButtonsContainer>
          {resetBtn}
          {submitButton}
        </SButtonsContainer>
      )}
    </SFlexColumnDiv>
  );
};

const STime = styled.div<{ status: recordingStatusEnum }>`
  color: black;
  font-size: 18px;
  font-weight: 700;
  margin-left: 8px;
  padding: 0 6px;
  /* Keep the timer width stable as digits change so the pill doesn't jump each second. */
  font-variant-numeric: tabular-nums;
  text-align: center;
`;

const SFlexColumnDiv = styled.div<{ status: recordingStatusEnum }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: ${({ status }) => (status === recordingStatusEnum.FINISHED ? '30px' : '32px')};
  padding-top: ${({ status }) =>
    status === recordingStatusEnum.FINISHED ? '0px' : '72px'};
  padding-bottom: ${({ status }) =>
    status === recordingStatusEnum.FINISHED ? '0px' : '40px'};

  @media (max-width: 768px) {
    gap: 12px;
  }
`;

const SRecordingStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin-right: 8px;
  padding: 0 10px;
`;

const SRecordInstruction = styled.div`
  font-size: 14px;
  line-height: 18px;
  letter-spacing: -0.02em;
  color: #807f91;
  margin-block-end: 22px;
  margin-block-start: -20px;
`;

const SRecordMessage = styled(SRecordInstruction)`
  color: #ff5858;
  text-align: center;
`;

const SWaveformContainer = styled.div`
  position: relative;
  height: 60px;
  width: 100%;
  max-width: 420px;
  margin-block-start: 0;
  margin-block-end: 0;
`;

const SWave = styled.div`
  width: 100%;
  height: 60px;
  overflow: hidden;
`;

const SRecordBtnGroup = styled.div<{ status: recordingStatusEnum }>`
  display: flex;
  align-items: center;
`;

const SButtonsContainer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 4px;
`;
